/**
 * Dataset 조사 사실 — Dataset 마다 미리 조사해 둔 끝점 · 손 축 · 그리퍼 · 결함 사실.
 *
 * 정본은 BE `packages/spnp/src/spnp/service/facts/type.py` 다. 사실은 두 상태로 산다. **active** 는 지금 믿는
 * 값이고, **candidate** 는 아직 확정하지 않은 제안(0..1)이다. 둘은 같은 모양의 문서다.
 *
 * 칸 하나는 값 · 근거 종류 · 출처 메모 세 가지다. 셋은 함께 비거나 함께 찬다. 함께 빈 칸의 뜻("자료 없음 →
 * 직접 조사")은 wire 경계(`api/datasetFactsApi.ts`)가 소유한다.
 *
 * 칸 이름은 wire 경로(`end_pose.point`)를 그대로 쓴다. BE · MCP · 이력이 같은 경로로 칸을 가리키기 때문이다.
 *
 * Owns:
 * - 칸 경로 · 묶음 · 어휘 상수, 문서 타입, 빈 문서, 두 문서의 비교, 서버와 같은 유효성 검사.
 *
 * Does not own:
 * - wire 읽기 · 쓰기 — `api/datasetFactsApi.ts`. 칸 이름 · 오류의 문구 — `feature/datasetFactsFormat.ts`.
 * - 최종 판정 — BE 가 422 로 거부한다. 이 검사는 보내기 전에 같은 규칙을 먼저 본다.
 */

export const FACT_BASES = [
  "source_declared",
  "documented",
  "measured",
  "inferred",
  "assumed",
] as const;
export type FactBasis = (typeof FACT_BASES)[number];

export const ORIGIN_POINTS = [
  "flange",
  "sigmakit_closed_tcp",
  "other_fixed",
  "grip_center",
] as const;
export type OriginPoint = (typeof ORIGIN_POINTS)[number];

export const QUATERNION_ORDERS = ["xyzw", "wxyz"] as const;
export type QuaternionOrder = (typeof QUATERNION_ORDERS)[number];

export const LENGTH_UNITS = ["m", "cm", "mm"] as const;
export type LengthUnit = (typeof LENGTH_UNITS)[number];

export const AXES = ["+x", "-x", "+y", "-y", "+z", "-z"] as const;
export type Axis = (typeof AXES)[number];

export const DEFECT_STATES = ["present", "absent"] as const;
export type DefectState = (typeof DEFECT_STATES)[number];

/** 쓰기를 보낸 곳. 기록용 힌트이고 권한이 아니다(요청 헤더 `X-Spnp-Client`). */
export const FACT_CLIENTS = ["fe", "mcp", "unknown"] as const;
export type FactClient = (typeof FACT_CLIENTS)[number];

export const FACT_EVENT_ACTIONS = ["proposed", "confirmed", "discarded"] as const;
export type FactEventAction = (typeof FACT_EVENT_ACTIONS)[number];

/** BE `Text` · 매핑 `reference_frame` · 채널 key 의 길이 상한. 글자(code point) 수다. */
export const NOTE_MAX_LENGTH = 2000;
export const FRAME_MAX_LENGTH = 64;
export const CHANNEL_MAX_LENGTH = 255;

export type Fact<T> = {
  readonly value: T | null;
  readonly basis: FactBasis | null;
  readonly note: string | null;
};

/** 칸 하나의 세 입력. 검사 문제가 어느 입력을 고치라는지 가리킨다. */
export type FactField = keyof Fact<unknown>;

export const FACT_GROUPS = ["end_pose", "hand_axes", "gripper", "defects"] as const;
export type FactGroup = (typeof FACT_GROUPS)[number];

/** 칸 경로와 값 타입. 순서가 화면의 순서다. */
type FactValueByPath = {
  "end_pose.point": OriginPoint;
  "end_pose.frame": string;
  "end_pose.quaternion_order": QuaternionOrder;
  "end_pose.position_unit": LengthUnit;
  "hand_axes.approach": Axis;
  "hand_axes.up": Axis;
  "gripper.channel": string;
  "gripper.open_value": number;
  "gripper.closed_value": number;
  "defects.quaternion_norm": DefectState;
  "defects.channel_names": DefectState;
};
export type FactPath = keyof FactValueByPath;

export const FACT_PATHS = [
  "end_pose.point",
  "end_pose.frame",
  "end_pose.quaternion_order",
  "end_pose.position_unit",
  "hand_axes.approach",
  "hand_axes.up",
  "gripper.channel",
  "gripper.open_value",
  "gripper.closed_value",
  "defects.quaternion_norm",
  "defects.channel_names",
] as const satisfies readonly FactPath[];

/** 사실 문서 한 벌. 칸은 언제나 11개다. */
export type DatasetFacts = { readonly [P in FactPath]: Fact<FactValueByPath[P]> };

/** 칸 하나의 입력 방식. 어휘 칸은 고르고, 이름 칸은 쓰고, 끝점 값은 숫자다. */
export type FactInput =
  | { readonly kind: "choice"; readonly options: readonly string[] }
  | { readonly kind: "text"; readonly maxLength: number }
  | { readonly kind: "number" };

export const FACT_INPUTS: Readonly<Record<FactPath, FactInput>> = {
  "end_pose.point": { kind: "choice", options: ORIGIN_POINTS },
  "end_pose.frame": { kind: "text", maxLength: FRAME_MAX_LENGTH },
  "end_pose.quaternion_order": { kind: "choice", options: QUATERNION_ORDERS },
  "end_pose.position_unit": { kind: "choice", options: LENGTH_UNITS },
  "hand_axes.approach": { kind: "choice", options: AXES },
  "hand_axes.up": { kind: "choice", options: AXES },
  "gripper.channel": { kind: "text", maxLength: CHANNEL_MAX_LENGTH },
  "gripper.open_value": { kind: "number" },
  "gripper.closed_value": { kind: "number" },
  "defects.quaternion_norm": { kind: "choice", options: DEFECT_STATES },
  "defects.channel_names": { kind: "choice", options: DEFECT_STATES },
};

/** 화면 · 편집이 칸을 값 타입 없이 다룰 때의 모양. */
export type AnyFact = Fact<string | number>;

export type FactsCandidate = {
  readonly facts: DatasetFacts;
  /** 제안한 Member 의 uuid. */
  readonly proposedBy: string;
  readonly client: FactClient;
  readonly modifiedAtMs: number;
};

/** GET 과 모든 쓰기의 응답. `revision` 은 쓰기의 `expected_revision` 이 되는 토큰이다. */
export type DatasetFactsRecord = {
  readonly datasetUuid: string;
  readonly revision: number;
  readonly active: DatasetFacts;
  readonly candidate: FactsCandidate | null;
};

/** 이력 한 줄의 전 · 후 상태. `candidate` 는 제안 문서만 담는다. */
export type FactsSnapshot = {
  readonly active: DatasetFacts;
  readonly candidate: DatasetFacts | null;
};

export type FactsEvent = {
  readonly uuid: string;
  readonly action: FactEventAction;
  readonly actorMemberUuid: string;
  readonly client: FactClient;
  /** 이 쓰기 뒤의 revision. */
  readonly revision: number;
  readonly before: FactsSnapshot;
  readonly after: FactsSnapshot;
  readonly createdAtMs: number;
};

const EMPTY_FACT: Fact<never> = { value: null, basis: null, note: null };

/** 모든 칸이 "자료 없음" 인 문서. active 행이 아직 없을 때의 값과 같다. */
export function emptyFacts(): DatasetFacts {
  return {
    "end_pose.point": EMPTY_FACT,
    "end_pose.frame": EMPTY_FACT,
    "end_pose.quaternion_order": EMPTY_FACT,
    "end_pose.position_unit": EMPTY_FACT,
    "hand_axes.approach": EMPTY_FACT,
    "hand_axes.up": EMPTY_FACT,
    "gripper.channel": EMPTY_FACT,
    "gripper.open_value": EMPTY_FACT,
    "gripper.closed_value": EMPTY_FACT,
    "defects.quaternion_norm": EMPTY_FACT,
    "defects.channel_names": EMPTY_FACT,
  };
}

export function factGroupOf(path: FactPath): FactGroup {
  return path.slice(0, path.indexOf(".")) as FactGroup;
}

/** 칸 경로의 묶음 안 이름(`end_pose.point` → `point`). */
export function factFieldOf(path: FactPath): string {
  return path.slice(path.indexOf(".") + 1);
}

/**
 * 칸 하나를 바꾼 새 문서. 값의 타입은 칸의 입력 방식(`FACT_INPUTS`)이 정한다 — 화면이 그 방식으로만 값을
 * 만들므로 여기서는 다시 좁히지 않는다.
 */
export function withFact(facts: DatasetFacts, path: FactPath, fact: AnyFact): DatasetFacts {
  return { ...facts, [path]: fact } as DatasetFacts;
}

export function isEmptyFact(fact: AnyFact): boolean {
  return fact.value === null && fact.basis === null && fact.note === null;
}

export function sameFact(left: AnyFact, right: AnyFact): boolean {
  return left.value === right.value && left.basis === right.basis && left.note === right.note;
}

export function sameFacts(left: DatasetFacts, right: DatasetFacts): boolean {
  return FACT_PATHS.every((path) => sameFact(left[path], right[path]));
}

/** 두 문서에서 값이 다른 칸. 화면 순서를 따른다. */
export function changedFactPaths(before: DatasetFacts, after: DatasetFacts): FactPath[] {
  return FACT_PATHS.filter((path) => !sameFact(before[path], after[path]));
}

/**
 * 이력 한 줄이 바꾼 칸. 확정은 active 를, 제안은 그 전의 기준(candidate, 없으면 active)과 새 candidate 를 비교한다.
 * 폐기는 칸을 바꾸지 않는다.
 */
export function eventChangedPaths(event: FactsEvent): FactPath[] {
  switch (event.action) {
    case "confirmed":
      return changedFactPaths(event.before.active, event.after.active);
    case "proposed":
      return changedFactPaths(
        event.before.candidate ?? event.before.active,
        event.after.candidate ?? event.after.active,
      );
    case "discarded":
      return [];
  }
}

export type FactIssueCode =
  /** 값 · 근거 · 메모 중 일부만 찼다. */
  | "partial"
  /** 메모가 공백뿐이다. */
  | "note_blank"
  /** 메모가 저장할 수 없는 문자(NUL · 짝 없는 surrogate)를 담는다. */
  | "note_bad_char"
  | "note_too_long"
  /** 이름(좌표계 · 채널)이 공백뿐이다. */
  | "text_blank"
  /** 이름이 저장할 수 없는 문자를 담는다. */
  | "text_bad_char"
  | "text_too_long"
  | "not_finite"
  /** approach 와 up 이 같은 축 문자다(평행). */
  | "axes_parallel"
  /** 열림 값과 닫힘 값이 같다. */
  | "gripper_same";

export type FactIssue = {
  readonly path: FactPath;
  readonly code: FactIssueCode;
  /** 고칠 입력. `partial` 은 비어 있는 입력들이고, 나머지는 문제를 가진 입력 하나다. */
  readonly fields: readonly FactField[];
};

/**
 * 서버 규칙과 같은 검사. 문제가 있으면 보내지 않는다. 문제는 화면 순서이고 칸마다 하나다 — 같은 칸의 문제를
 * 여러 줄로 쌓으면 무엇부터 고칠지 흐려진다.
 */
export function factIssues(facts: DatasetFacts): FactIssue[] {
  const issues: FactIssue[] = [];
  for (const path of FACT_PATHS) {
    const issue = factIssue(facts, path);
    if (issue !== null) {
      issues.push({ path, ...issue });
    }
  }
  return issues;
}

function factIssue(facts: DatasetFacts, path: FactPath): Omit<FactIssue, "path"> | null {
  const fact = facts[path];
  if (isEmptyFact(fact)) {
    return null;
  }
  const missing = (["value", "basis", "note"] as const).filter((field) => fact[field] === null);
  if (missing.length > 0) {
    return { code: "partial", fields: missing };
  }
  const note = textIssue(fact.note ?? "", NOTE_MAX_LENGTH);
  if (note !== null) {
    return { code: `note_${note}`, fields: ["note"] };
  }
  const input = FACT_INPUTS[path];
  if (input.kind === "text" && typeof fact.value === "string") {
    const text = textIssue(fact.value, input.maxLength);
    if (text !== null) return { code: `text_${text}`, fields: ["value"] };
  }
  if (input.kind === "number" && (typeof fact.value !== "number" || !Number.isFinite(fact.value))) {
    return { code: "not_finite", fields: ["value"] };
  }
  if (path === "hand_axes.up" && parallelAxes(facts)) {
    return { code: "axes_parallel", fields: ["value"] };
  }
  if (path === "gripper.closed_value" && facts["gripper.open_value"].value === fact.value) {
    return { code: "gripper_same", fields: ["value"] };
  }
  return null;
}

/**
 * Python `str.isspace()` 가 참인 글자 — BE `Text` 가 `str.strip()` 으로 지우는 공백이다. JS `trim()` 과 다르다.
 * U+001C–U+001F · U+0085 는 공백이고 U+FEFF 는 글자다.
 */
const PYTHON_SPACES: ReadonlySet<number> = new Set([
  0x09, 0x0a, 0x0b, 0x0c, 0x0d, 0x1c, 0x1d, 0x1e, 0x1f, 0x20, 0x85, 0xa0, 0x1680, 0x2000, 0x2001,
  0x2002, 0x2003, 0x2004, 0x2005, 0x2006, 0x2007, 0x2008, 0x2009, 0x200a, 0x2028, 0x2029, 0x202f,
  0x205f, 0x3000,
]);

/**
 * BE `Text` 와 같은 검사. 공백 아닌 글자가 하나는 있어야 하고, NUL 과 짝 없는 surrogate 는 저장할 수 없다. 길이는
 * BE(Python `len`)처럼 글자(code point) 수다 — 이모지 하나는 UTF-16 두 단위지만 한 글자다.
 */
function textIssue(value: string, maxLength: number): "blank" | "bad_char" | "too_long" | null {
  let length = 0;
  let blank = true;
  let badChar = false;
  // 문자열 순회는 code point 단위다. 짝 맞은 surrogate 는 한 번, 짝 없는 surrogate 는 그 단위 하나로 나온다.
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    length += 1;
    if (!PYTHON_SPACES.has(code)) blank = false;
    if (code === 0 || (code >= 0xd800 && code <= 0xdfff)) badChar = true;
  }
  if (blank) return "blank";
  if (badChar) return "bad_char";
  return length > maxLength ? "too_long" : null;
}

/** `+z` 와 `-z` 처럼 축 문자가 같으면 평행이다. */
function parallelAxes(facts: DatasetFacts): boolean {
  const approach = facts["hand_axes.approach"].value;
  const up = facts["hand_axes.up"].value;
  return approach !== null && up !== null && approach.slice(1) === up.slice(1);
}
